
import { useState, useCallback } from 'react';

export interface NotificationSound {
  enabled: boolean;
  volume: number;
  urgentOnly?: boolean;
}

export const useNotificationSound = () => {
  const [soundSettings, setSoundSettings] = useState<NotificationSound>({
    enabled: true,
    volume: 0.5,
    urgentOnly: false,
  });

  const playNotificationSound = useCallback((priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium') => {
    if (!soundSettings.enabled) return;
    if (soundSettings.urgentOnly && priority !== 'urgent') return;

    // Create audio context for better browser support
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Different frequencies for different priorities
    const frequencies = {
      low: 400,
      medium: 600,
      high: 800,
      urgent: 1000
    };

    const frequency = frequencies[priority];
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    // Set volume
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(soundSettings.volume * 0.3, audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);

    // Cleanup
    setTimeout(() => {
      audioContext.close();
    }, 500);
  }, [soundSettings]);

  return {
    soundSettings,
    setSoundSettings,
    playNotificationSound,
  };
};
